import { authService, notifyAuthStorageChanged } from './auth';
import { AUTH_REQUIRED_MESSAGE } from '../utils/customerAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export class CustomerApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'CustomerApiError';
    this.status = status;
    this.code = code;
  }
}

/** Authenticated shopper API calls with mandatory token + 401 session handling. */
export async function customerFetch(
  path: string,
  options: RequestInit = {},
  { requireAuth = true }: { requireAuth?: boolean } = {}
): Promise<Response> {
  const token = authService.getToken();
  if (requireAuth && !token) {
    throw new CustomerApiError(AUTH_REQUIRED_MESSAGE, 401, 'AUTH_REQUIRED');
  }

  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    authService.clearAuthData();
    notifyAuthStorageChanged();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('nls-session-expired'));
    }
    const data = await response.json().catch(() => ({}));
    throw new CustomerApiError(
      data.message || 'Session expired. Please sign in again.',
      401,
      data.code || 'SESSION_EXPIRED'
    );
  }

  return response;
}

export async function customerJson<T = unknown>(
  path: string,
  options?: RequestInit,
  opts?: { requireAuth?: boolean }
): Promise<T> {
  const response = await customerFetch(path, options, opts);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new CustomerApiError(
      (data as { message?: string }).message || `Request failed (${response.status})`,
      response.status,
      (data as { code?: string }).code
    );
  }
  return data as T;
}
